/**
 * Utilities for simplifying GPS track points using the Douglas-Peucker algorithm.
 */

const EARTH_RADIUS_METERS = 6_371_000
const DEG_TO_RAD = Math.PI / 180

export interface TrackPointLike {
  lat: number
  lon: number
  ele?: number
  time?: number
  clientId?: string
}

interface ProjectedPoint {
  x: number
  y: number
}

const projectToMeters = (point: TrackPointLike, reference: TrackPointLike): ProjectedPoint => {
  const referenceLatRad = reference.lat * DEG_TO_RAD
  const cosReferenceLat = Math.cos(referenceLatRad)

  const latRad = point.lat * DEG_TO_RAD
  const lonRad = point.lon * DEG_TO_RAD
  const referenceLonRad = reference.lon * DEG_TO_RAD

  return {
    x: (lonRad - referenceLonRad) * cosReferenceLat * EARTH_RADIUS_METERS,
    y: (latRad - referenceLatRad) * EARTH_RADIUS_METERS,
  }
}

const perpendicularDistanceSquared = (
  point: ProjectedPoint,
  start: ProjectedPoint,
  end: ProjectedPoint,
) => {
  const segmentX = end.x - start.x
  const segmentY = end.y - start.y

  if (segmentX === 0 && segmentY === 0) {
    const dx = point.x - start.x
    const dy = point.y - start.y
    return dx * dx + dy * dy
  }

  const t =
    ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) /
    (segmentX * segmentX + segmentY * segmentY)

  const clampedT = Math.max(0, Math.min(1, t))
  const projectionX = start.x + clampedT * segmentX
  const projectionY = start.y + clampedT * segmentY

  const dx = point.x - projectionX
  const dy = point.y - projectionY

  return dx * dx + dy * dy
}

/**
 * Simplify track points using Douglas-Peucker with a given tolerance in meters.
 * Returns a new array while preserving all properties on retained points.
 */
export const simplifyTrackPoints = <T extends TrackPointLike>(
  points: readonly T[],
  toleranceMeters: number = 5,
): T[] => {
  if (points.length <= 2 || toleranceMeters <= 0) {
    return points.slice() as T[]
  }

  const referencePoint = points[0]
  const projectedPoints = points.map((point) => projectToMeters(point, referencePoint))
  const keepMask = new Uint8Array(points.length)

  const squareTolerance = toleranceMeters * toleranceMeters
  const stack: Array<[number, number]> = [[0, points.length - 1]]

  keepMask[0] = 1
  keepMask[points.length - 1] = 1

  while (stack.length > 0) {
    const [startIndex, endIndex] = stack.pop() as [number, number]
    if (endIndex - startIndex <= 1) {
      continue
    }

    const startPoint = projectedPoints[startIndex]
    const endPoint = projectedPoints[endIndex]

    let maxDistanceSq = 0
    let indexOfMax = -1

    for (let i = startIndex + 1; i < endIndex; i += 1) {
      const distanceSq = perpendicularDistanceSquared(projectedPoints[i], startPoint, endPoint)
      if (distanceSq > maxDistanceSq) {
        maxDistanceSq = distanceSq
        indexOfMax = i
      }
    }

    if (indexOfMax !== -1 && maxDistanceSq > squareTolerance) {
      keepMask[indexOfMax] = 1
      stack.push([startIndex, indexOfMax], [indexOfMax, endIndex])
    }
  }

  const simplified: T[] = []
  for (let i = 0; i < points.length; i += 1) {
    if (keepMask[i]) {
      simplified.push(points[i])
    }
  }

  if (simplified.length < 2) {
    return [points[0], points[points.length - 1]] as T[]
  }

  return simplified
}
